import React from 'react'
import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEllipsisVertical, faBars, faAngleRight } from '@fortawesome/free-solid-svg-icons'
import '../../../assets/css/pages/Expeditions/StudentTag.css'
import _default from 'userflow.js'
import {
	useIsInViewport,
	useGetAbsElementPosition,
	useViewPortObserver
} from '../../../customeHooks'
import { ExpeditionContext, UNGROUPED, MOVE, SWAP, useExpeditionUtils } from '../Expedition_utils'
import { handleClickOutside } from '../../../util'

const StudentTag = ({ view, student, location }) => {
	const [isContextMenuOpen, setIsContextMenuOpen] = React.useState(false)
	const { isDelete } = React.useContext(ExpeditionContext)
	const StudentTagRef = React.useRef(null)
	const { absPosition } = useGetAbsElementPosition(StudentTagRef)
	const size = useViewPortObserver(StudentTagRef)
	const handleCloseContextMenu = () => {
		setIsContextMenuOpen(false)
	}

	return (
		<div
			ref={StudentTagRef}
			key={Object.keys(student)[0]}
			className='StudentTags'
			onContextMenu={e => {
				e.preventDefault()
				setIsContextMenuOpen(prev => (isDelete ? false : !prev))
			}}>
			{!isDelete && <FontAwesomeIcon icon={faBars} className='StudentTags__moveHandle' />}

			<span className={`StudentTags__name ${isDelete && '--deleteMode'}`}>
				{student[Object.keys(student)]}
			</span>

			{!isDelete && (
				<button
					className='StudentTags__dropDownHandle'
					onClick={() => {
						setIsContextMenuOpen(prev => !prev)
					}}>
					<FontAwesomeIcon
						icon={faEllipsisVertical}
						className='StudentTags__dropDownHandle--icon'
					/>
				</button>
			)}

			{isContextMenuOpen && (
				<StudentTagContextMenu
					view={view}
					absPosition={absPosition}
					size={size}
					studentName={student[Object.keys(student)]}
					location={location}
					isContextMenuOpen={isContextMenuOpen}
					handleCloseContextMenu={handleCloseContextMenu}
				/>
			)}
		</div>
	)
}

StudentTag.propTypes = {
	view: PropTypes.string,
	student: PropTypes.object.isRequired,
	location: PropTypes.object.isRequired
}

const StudentTagContextMenu = ({ view, location, isContextMenuOpen, handleCloseContextMenu }) => {
	const { studentGroups } = React.useContext(ExpeditionContext)
	const { handleRemoveStudent } = useExpeditionUtils()
	const [isOpenMoveOptionMenu, setIsOpenMoveOptionMenu] = React.useState({
		show: false,
		mode: ''
	})

	const ContextMenuRef = React.useRef(null)
	const { directionName } = useIsInViewport(ContextMenuRef)
	const handleCloseMoveOptionMenu = () => {
		setIsOpenMoveOptionMenu({ show: false })
	}

	React.useEffect(() => {
		if (isContextMenuOpen) {
			handleClickOutside('mousedown', ContextMenuRef, () => handleCloseContextMenu())
		}
	}, [ContextMenuRef])

	return (
		<div
			className={`StudentTagContextMenu ${directionName}`}
			ref={ContextMenuRef}
			style={directionName ? { bottom: 0 } : {}}
			onMouseLeave={() => {
				handleCloseMoveOptionMenu()
			}}>
			<ul className='StudentTagContextMenu__list'>
				{view !== UNGROUPED && (
					<li
						className='StudentTagContextMenu__list--swap'
						onMouseEnter={() => {
							setIsOpenMoveOptionMenu({ show: true, mode: SWAP })
						}}>
						<span>Swap with</span>
						<FontAwesomeIcon icon={faAngleRight} />
						{isOpenMoveOptionMenu.show && isOpenMoveOptionMenu.mode === SWAP && (
							<MoveOptionMenu
								mode={SWAP}
								actionTarget={location}
								options={studentGroups}
								handleCloseMoveOptionMenu={handleCloseMoveOptionMenu}
							/>
						)}
					</li>
				)}

				<li
					className='StudentTagContextMenu__list--move'
					onMouseEnter={() => {
						setIsOpenMoveOptionMenu({ show: true, mode: MOVE })
					}}>
					<span>Move to group</span>
					<FontAwesomeIcon icon={faAngleRight} />
					{isOpenMoveOptionMenu.show && isOpenMoveOptionMenu.mode === MOVE && (
						<MoveOptionMenu
							mode={MOVE}
							actionTarget={location}
							options={studentGroups}
							handleCloseMoveOptionMenu={handleCloseMoveOptionMenu}
						/>
					)}
				</li>

				{view !== UNGROUPED && (
					<li
						className='StudentTagContextMenu__list--remove'
						onMouseEnter={() => {
							setIsOpenMoveOptionMenu({ show: false })
						}}>
						<button
							onClick={() => {
								handleRemoveStudent(location)
							}}>
							Remove from this group
						</button>
					</li>
				)}
			</ul>
		</div>
	)
}

StudentTagContextMenu.propTypes = {
	view: PropTypes.string,
	absPosition: PropTypes.object,
	size: PropTypes.object,
	location: PropTypes.object,
	handleCloseContextMenu: PropTypes.func.isRequired
}

const MoveOptionMenu = ({ actionTarget, options, mode, handleCloseMoveOptionMenu }) => {
	const MoveOptionMenuRef = React.useRef(null)
	const { directionName } = useIsInViewport(MoveOptionMenuRef)

	const { handleMoveStudentGroup, handleSwapStudentGroup } = useExpeditionUtils()

	const handleGenerateSwappableStudentList = (targetStudent, availableGroups) => {
		/*
			[{groupIndex: groupIndex, studentIndex:studentIndex, studentFullName: studentFullName}]
		*/
		const swappableStudent = {
			groups: [...availableGroups.groups],
			ungroupedStudents: [...availableGroups?.ungroupedStudents]
		}

		let groupedStudents = _.map(swappableStudent.groups, (group, groupIndex) => {
			const students = _.map(group, (student, studentIndex) => {
				if (
					!(
						groupIndex === targetStudent.groupIndex &&
						studentIndex === targetStudent.studentIndex
					)
				) {
					return {
						groupIndex: groupIndex,
						studentIndex: studentIndex,
						studentFullName: student[Object.keys(student)]
					}
				}
				return {}
			})
			return _.sortBy(students, student => student.studentFullName)
		})

		//TODO: not sure if ungrouped students need to be swappable, this function is currently hidden for ungrouped students
		let ungroupedStudents = []
		if (!_.isEmpty(swappableStudent.ungroupedStudents)) {
			ungroupedStudents = _.map(
				swappableStudent.ungroupedStudents,
				(student, studentIndex) => ({
					groupIndex: 'ungroupedStudent',
					studentIndex: studentIndex,
					studentFullName: student[Object.keys(student)]
				})
			)
			_.sortBy(ungroupedStudents, student => student.studentFullName)
		}

		return _.flatten(groupedStudents)
	}

	const handleGenerateMoveList = availableGroups => {
		return _.map(availableGroups.groups, (group, ind) => ({
			group: `Group ${ind + 1}`
		}))
	}

	return (
		<div
			className={`MoveOptionMenu ${directionName}`}
			ref={MoveOptionMenuRef}
			onMouseLeave={() => {
				handleCloseMoveOptionMenu()
			}}>
			<ul className={`MoveOptionMenu__list`}>
				{mode === MOVE &&
					handleGenerateMoveList(options).map((option, ind) => {
						return (
							<li
								className='MoveOptionMenu'
								key={`MoveOptionMenu-move-${option.group}`}
								onClick={() => {
									handleMoveStudentGroup(actionTarget, ind)
								}}>
								{option.group}
							</li>
						)
					})}
				{mode === SWAP &&
					handleGenerateSwappableStudentList(actionTarget, options).map((option, ind) => {
						return (
							!_.isEmpty(option) && (
								<li
									className='MoveOptionMenu'
									key={`MoveOptionMenu-swap-${option.groupIndex}-${option.studentIndex}-${option.studentFullName}`}
									onClick={() => {
										handleSwapStudentGroup(actionTarget, {
											groupIndex: option.groupIndex,
											studentIndex: option.studentIndex
										})
									}}>
									<span>{option.studentFullName}</span>
									<span>{`Group ${option.groupIndex + 1}`}</span>
								</li>
							)
						)
					})}
			</ul>
		</div>
	)
}
MoveOptionMenu.propTypes = {
	actionTarget: PropTypes.object.isRequired,
	options: PropTypes.object.isRequired,
	mode: PropTypes.string.isRequired,
	handleCloseMoveOptionMenu: PropTypes.func.isRequired
}

export default StudentTag
